/**
 * CredentialsPanel — manage exchange API keys.
 *
 * Displays stored credentials (masked), allows adding new ones,
 * testing connectivity, and deleting existing entries.
 */

import { useState } from 'react';
import {
  useCredentials,
  useCreateCredential,
  useDeleteCredential,
  useTestCredential,
} from '../../hooks/useCredentials';
import type { CredentialSummary } from '../../types';

// ============================================================================
// Add form
// ============================================================================

interface AddCredentialFormProps {
  onCancel: () => void;
  onSaved: () => void;
}

function AddCredentialForm({ onCancel, onSaved }: AddCredentialFormProps) {
  const [label, setLabel] = useState('');
  const [exchange, setExchange] = useState('bybit');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [testnet, setTestnet] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const createMutation = useCreateCredential();

  const inputClass =
    'w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent';

  const isDisabled =
    !label.trim() || !apiKey.trim() || !apiSecret.trim() || createMutation.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isDisabled) return;
    try {
      await createMutation.mutateAsync({
        label: label.trim(),
        exchange,
        apiKey: apiKey.trim(),
        apiSecret: apiSecret.trim(),
        testnet,
      });
      onSaved();
    } catch {
      // Error shown via createMutation.error
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 p-4 bg-gray-700/50 rounded-lg border border-gray-600">
      <h4 className="text-sm font-semibold text-white">Add API Key</h4>

      {/* Label */}
      <div>
        <label className="block text-xs text-gray-400 mb-1">Label</label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Bybit Main Account"
          className={inputClass}
          required
        />
      </div>

      {/* Exchange */}
      <div>
        <label className="block text-xs text-gray-400 mb-1">Exchange</label>
        <select
          value={exchange}
          onChange={(e) => setExchange(e.target.value)}
          className={inputClass}
        >
          <option value="bybit">Bybit</option>
        </select>
      </div>

      {/* API Key */}
      <div>
        <label className="block text-xs text-gray-400 mb-1">API Key</label>
        <div className="relative">
          <input
            type={showKey ? 'text' : 'password'}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Paste your API key"
            className={`${inputClass} pr-10`}
            required
            autoComplete="off"
          />
          <button
            type="button"
            onClick={() => setShowKey((v) => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            aria-label={showKey ? 'Hide API key' : 'Show API key'}
          >
            {showKey ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* API Secret */}
      <div>
        <label className="block text-xs text-gray-400 mb-1">API Secret</label>
        <div className="relative">
          <input
            type={showSecret ? 'text' : 'password'}
            value={apiSecret}
            onChange={(e) => setApiSecret(e.target.value)}
            placeholder="Paste your API secret"
            className={`${inputClass} pr-10`}
            required
            autoComplete="off"
          />
          <button
            type="button"
            onClick={() => setShowSecret((v) => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            aria-label={showSecret ? 'Hide API secret' : 'Show API secret'}
          >
            {showSecret ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Testnet */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="testnet-checkbox"
          checked={testnet}
          onChange={(e) => setTestnet(e.target.checked)}
          className="w-4 h-4 rounded accent-primary-500"
        />
        <label htmlFor="testnet-checkbox" className="text-sm text-gray-300 cursor-pointer">
          Testnet account
        </label>
      </div>

      {/* Error */}
      {createMutation.isError && (
        <div className="bg-red-900/50 border border-red-700 rounded p-2 text-xs text-red-300">
          {createMutation.error instanceof Error
            ? createMutation.error.message
            : 'Failed to save credential'}
        </div>
      )}

      {/* Buttons */}
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-1.5 rounded border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 transition-colors text-sm"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isDisabled}
          className="flex-1 py-1.5 rounded bg-primary-600 hover:bg-primary-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium text-sm transition-colors"
        >
          {createMutation.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// Credential row
// ============================================================================

interface CredentialRowProps {
  credential: CredentialSummary;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

function CredentialRow({ credential, onDelete, isDeleting }: CredentialRowProps) {
  const testMutation = useTestCredential();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const createdDate = new Date(credential.createdAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  function handleDeleteClick() {
    if (confirmDelete) {
      onDelete(credential.id);
      setConfirmDelete(false);
    } else {
      setConfirmDelete(true);
    }
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-3 border-b border-gray-700 last:border-0">
      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-white truncate">{credential.label}</span>
          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-700 text-gray-300 capitalize">
            {credential.exchange}
          </span>
          {credential.testnet && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-amber-900/60 text-amber-400 font-medium">
              Testnet
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          <span className="text-xs text-gray-500 font-mono">...{credential.maskedKey}</span>
          <span className="text-xs text-gray-600">{createdDate}</span>
        </div>

        {/* Test result */}
        {testMutation.isSuccess && testMutation.variables === credential.id && (
          <div className="mt-1">
            {testMutation.data.success ? (
              <span className="text-xs text-green-400">
                Connected — Total: ${testMutation.data.balance.total.toFixed(2)}, Available: ${testMutation.data.balance.available.toFixed(2)}
              </span>
            ) : (
              <span className="text-xs text-red-400">
                Failed: {testMutation.data.error}
              </span>
            )}
          </div>
        )}
        {testMutation.isError && testMutation.variables === credential.id && (
          <span className="text-xs text-red-400 mt-1 block">
            {testMutation.error instanceof Error ? testMutation.error.message : 'Test failed'}
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={() => testMutation.mutate(credential.id)}
          disabled={testMutation.isPending && testMutation.variables === credential.id}
          className="px-2 py-1 text-xs text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded transition-colors disabled:opacity-50"
        >
          {testMutation.isPending && testMutation.variables === credential.id
            ? 'Testing...'
            : 'Test'}
        </button>

        {confirmDelete ? (
          <div className="flex items-center gap-1">
            <span className="text-xs text-amber-400">Sure?</span>
            <button
              type="button"
              onClick={handleDeleteClick}
              disabled={isDeleting}
              className="px-2 py-1 text-xs text-white bg-red-700 hover:bg-red-600 rounded transition-colors disabled:opacity-50"
            >
              {isDeleting ? '...' : 'Yes'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="px-2 py-1 text-xs text-gray-400 hover:text-white bg-gray-700 rounded transition-colors"
            >
              No
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleDeleteClick}
            className="px-2 py-1 text-xs text-gray-400 hover:text-red-400 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded transition-colors"
            aria-label={`Delete credential ${credential.label}`}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main panel
// ============================================================================

export function CredentialsPanel() {
  const { data: credentials, isLoading, isError } = useCredentials();
  const deleteMutation = useDeleteCredential();
  const [showAddForm, setShowAddForm] = useState(false);

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
        <p className="text-sm text-gray-400">Loading API keys...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
        <p className="text-sm text-red-400">Failed to load API keys.</p>
      </div>
    );
  }

  const list = credentials ?? [];

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-100 uppercase tracking-wide">
          API Keys
        </h3>
        {!showAddForm && (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1 px-2 py-1 text-xs text-white bg-primary-600 hover:bg-primary-500 rounded transition-colors"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add API Key
          </button>
        )}
      </div>

      {/* Add form */}
      {showAddForm && (
        <AddCredentialForm
          onCancel={() => setShowAddForm(false)}
          onSaved={() => setShowAddForm(false)}
        />
      )}

      {/* List */}
      {list.length === 0 && !showAddForm ? (
        <p className="text-sm text-gray-500 py-2">
          No API keys configured. Add one to connect live exchange accounts.
        </p>
      ) : (
        <div>
          {list.map((cred) => (
            <CredentialRow
              key={cred.id}
              credential={cred}
              onDelete={(id) => deleteMutation.mutate(id)}
              isDeleting={deleteMutation.isPending && deleteMutation.variables === cred.id}
            />
          ))}
        </div>
      )}

      <p className="text-xs text-gray-500">
        API secrets are stored encrypted and never shown in full. Only the last 4 characters of the key are displayed.
      </p>
    </div>
  );
}
